import React, {useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {Box, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import Form, {MuiRFF, Yup} from '../../form';
import Grid2 from "@mui/material/Unstable_Grid2";
import TerritoryAutocomplete from "../../text-fields/autocomplete/territory-autocomplete";
import SalesPersonAutocomplete from "../../text-fields/autocomplete/sales-person-autocomplete";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";

const schema = Yup.object().shape({
    territory: Yup.mixed().nullable(),
    salesPerson: Yup.mixed().nullable(),
    accountManager: Yup.mixed().nullable(),
    termsCode: Yup.string().nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const UpsertOrderInfoDialog = ({
                                   open,
                                   close,
                                   entityToShow,
                                   salesPerson,
                                   accountManager,
                                   territory,
                                   termsCode,
                                   update,
                               }) => {
    const [paymentTerms, setPaymentTerms] = useState([]);
    const [loadingPaymentTerms, setLoadingPaymentTerms] = useState(false);
    const isMounted = useIsMounted();

    const initialValues = useMemo(() => {
        return {
            territory: entityToShow === 'territory' ? territory : null,
            salesPerson: entityToShow === 'salesPerson' ? salesPerson : null,
            accountManager: entityToShow === 'accountManager' ? accountManager : null,
            termsCode: entityToShow === 'termsCode' ? termsCode ?? null : null,
        }
    }, [entityToShow, salesPerson, territory, accountManager, termsCode])

    /**
     * Fetches payment terms when the dialog is opened and the entity to show is 'termsCode'.
     */
    useEffect(() => {
        if (!open || entityToShow !== 'termsCode') {
            return;
        }
        const getPaymentTerms = async () => {
            setLoadingPaymentTerms(true);
            const response = await Api.getAllPaymentTerms();
            if (!isMounted()) {
                return;
            }
            if (response?.resultFlag) {
                setPaymentTerms(response.data ?? []);
            }
            setLoadingPaymentTerms(false);
        };
        void getPaymentTerms();
    }, [entityToShow, isMounted, open]);

    /**
     * Handles the form submission.
     * @type {(function(*): Promise<void>)}
     */
    const submitForm = useCallback(async (values) => {
        update({
            territory: values.territory,
            salesPerson: values.salesPerson,
            accountManager: values.accountManager,
            termsCode: values.termsCode,
        });
        close();
    }, [close, update])

    const dialogTitle = useMemo(() => {
        switch (entityToShow) {
            case 'territory':
                return 'Territory';
            case 'accountManager':
                return 'Account Manager';
            case 'termsCode':
                return 'Payment Terms';
            default:
                return 'Sales Person';
        }
    }, [entityToShow]);

    return (
        <Dialog open={open} setOpen={close} maxWidth={'sm'}>
            <DialogTitle>
                Update {dialogTitle}
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    initialValues={initialValues}
                    onSubmit={submitForm}
                    validate={validate}
                    formProps={{id: 'upsert-order-info-dialog-form'}}
                >
                    <Grid2 spacing={2} container>
                        {
                            entityToShow === 'territory' &&
                            <Grid2 xs={12}>
                                <TerritoryAutocomplete
                                    form
                                    name={'territory'}
                                    label={'Territory'}
                                    required={required.territory}
                                />
                            </Grid2>
                        }
                        {
                            entityToShow === 'salesPerson' &&
                            <Grid2 xs={12}>
                                <SalesPersonAutocomplete
                                    form
                                    name={'salesPerson'}
                                    label={'Sales Person'}
                                    required={required.salesPerson}
                                />
                            </Grid2>
                        }
                        {
                            entityToShow === 'accountManager' &&
                            <Grid2 xs={12}>
                                <SalesPersonAutocomplete
                                    form
                                    name={'accountManager'}
                                    label={'Account Manager'}
                                    required={required.accountManager}
                                />
                            </Grid2>
                        }
                        {
                            entityToShow === 'termsCode' &&
                            <Grid2 xs={12}>
                                <OutlinedDropdownForm
                                    name={'termsCode'}
                                    label={'Payment Terms'}
                                    required={required.termsCode}
                                    disabled={loadingPaymentTerms}
                                    renderValue={v => paymentTerms?.find(e => e.code === v)?.description ?? v ?? ''}
                                    data={paymentTerms?.map(e => ({
                                        value: e.code,
                                        label: `${e.description} (${e.code})`,
                                        id: e.code,
                                    })) ?? []}
                                />
                            </Grid2>
                        }
                    </Grid2>
                </Form>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        type={'submit'}
                        form={'upsert-order-info-dialog-form'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        Save
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
export default UpsertOrderInfoDialog;
